// ---------------------------------------------------------------------------
// Course Component: Quiz Block — Scored Assessment
// ---------------------------------------------------------------------------
// Multiple choice + true/false questions. Scored with pass/fail threshold.
// Emits quiz.completed event with score. Can gate drip release.

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr, text } from "../escape.js";

export interface QuizBlockProps {
  title: string;
  description?: string;
  questions: QuizQuestion[];
  passingScore: number; // 0-100
  maxAttempts: number;
  shuffleQuestions: boolean;
  shuffleOptions: boolean;
  showExplanations: boolean;
  timeLimitMinutes?: number;
}

export interface QuizQuestion {
  id: string;
  type: "multiple_choice" | "true_false";
  text: string;
  options: QuizOption[];
  correctOptionId: string;
  points: number;
  explanation?: string;
}

export interface QuizOption {
  id: string;
  text: string;
}

export interface QuizAttemptResult {
  score: number; // 0-100
  passed: boolean;
  totalPoints: number;
  earnedPoints: number;
  answers: Array<{ questionId: string; selectedOptionId: string; correct: boolean }>;
  attemptNumber: number;
  durationSeconds: number;
}

/**
 * Grade a quiz attempt.
 */
export function gradeQuiz(
  questions: QuizQuestion[],
  answers: Record<string, string>, // questionId → selectedOptionId
): { score: number; totalPoints: number; earnedPoints: number; results: QuizAttemptResult["answers"] } {
  let totalPoints = 0;
  let earnedPoints = 0;
  const results: QuizAttemptResult["answers"] = [];

  for (const q of questions) {
    totalPoints += q.points;
    const selected = answers[q.id];
    const correct = selected === q.correctOptionId;
    if (correct) earnedPoints += q.points;
    results.push({ questionId: q.id, selectedOptionId: selected ?? "", correct });
  }

  const score = totalPoints > 0 ? Math.round((earnedPoints / totalPoints) * 100) : 0;
  return { score, totalPoints, earnedPoints, results };
}

/**
 * Shuffle array using Fisher-Yates. Uses Math.random() — acceptable for
 * UX randomization (quiz question order). NOT for security operations.
 */
export function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j]!, shuffled[i]!];
  }
  return shuffled;
}

export const QUIZ_BLOCK_DEF = {
  type: "quiz-block" as const,
  label: "Quiz",
  category: "course",
  defaultProps: {
    title: "Quiz",
    questions: [],
    passingScore: 70,
    maxAttempts: 3,
    shuffleQuestions: false,
    shuffleOptions: false,
    showExplanations: true,
  } satisfies Partial<QuizBlockProps>,
};

function render(props: Record<string, unknown>, _ctx: RenderContext): string {
  const p = props as unknown as QuizBlockProps;
  const questions = Array.isArray(p.questions) ? p.questions : [];

  const questionHtml = questions
    .map((question, index) => {
      const optionsHtml = question.options
        .map(
          (option) =>
            `        <li class="ease-course-quiz__option">${text(option.text)}</li>`,
        )
        .join("\n");

      return `    <article class="ease-course-quiz__question">
      <header class="ease-course-quiz__question-header">
        <span class="ease-course-quiz__question-index">Question ${index + 1}</span>
        <span class="ease-course-quiz__question-points">${text(String(question.points))} pt</span>
      </header>
      <p class="ease-course-quiz__question-text">${text(question.text)}</p>
      <ol class="ease-course-quiz__options">
${optionsHtml}
      </ol>
    </article>`;
    })
    .join("\n");

  return `<section class="ease-section ease-course-quiz" aria-label="${attr(p.title ?? "Quiz")}">
  <div class="ease-course-quiz__card">
    <header class="ease-course-quiz__header">
      <h2 class="ease-course-quiz__title">${text(p.title ?? "Quiz")}</h2>
      ${p.description ? `<p class="ease-course-quiz__description">${text(p.description)}</p>` : ""}
      <div class="ease-course-quiz__meta">
        <span>Pass score: ${text(String(p.passingScore ?? 70))}%</span>
        <span>Attempts: ${text(String(p.maxAttempts ?? 3))}</span>
        <span>Questions: ${text(String(questions.length))}</span>
      </div>
    </header>
${questionHtml || '    <p class="ease-course-quiz__empty">Add questions to configure this quiz.</p>'}
  </div>
</section>`;
}

registerRenderer("quiz-block", { render });
